import { useState, useEffect, useCallback } from 'react';

const API = '/api/cms/controllers';

function authHeaders(): Record<string, string> {
  const token = sessionStorage.getItem('gfs_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ControllerMedia {
  id: string;
  product_id: string;
  public_url: string;
  storage_path: string | null;
  mime_type: string;
  caption: string;
  is_primary: boolean;
  focal_x: number;
  focal_y: number;
  sort_order: number;
  created_at: string;
}

export interface ControllerProduct {
  id: string;
  slug: string;
  name: string;
  short_description: string;
  description: string;
  price: number;
  compare_at_price: number | null;
  status: 'active' | 'draft' | 'hidden';
  gallery_type: 'custom' | 'standard' | 'edge';
  featured: boolean;
  availability: 'available' | 'sold';
  sort_order: number;
  created_at: string;
  updated_at: string;
  media?: ControllerMedia[];
}

export interface GalleryItem {
  id: string;
  name: string;
  images: string[];
  basePrice: number;
}

export function toGalleryItem(p: ControllerProduct): GalleryItem {
  const sorted = [...(p.media ?? [])].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return a.sort_order - b.sort_order;
  });
  return {
    id: p.slug,
    name: p.name,
    images: sorted.map(m => m.public_url).filter(Boolean),
    basePrice: p.price,
  };
}

// ── API functions ─────────────────────────────────────────────────────────────

export async function fetchControllers(adminMode = false): Promise<ControllerProduct[]> {
  const url = adminMode ? `${API}?admin=1` : API;
  const headers = adminMode ? { ...authHeaders() } : {};
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
  const data = await res.json() as { products: ControllerProduct[] };
  return data.products;
}

export async function fetchController(id: string): Promise<ControllerProduct> {
  const res = await fetch(`${API}/${id}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
  const data = await res.json() as { product: ControllerProduct };
  return data.product;
}

export async function createController(fields: Partial<ControllerProduct>): Promise<ControllerProduct> {
  const res = await fetch(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Create failed (${res.status})`);
  }
  const data = await res.json() as { product: ControllerProduct };
  return data.product;
}

export async function updateController(id: string, fields: Partial<ControllerProduct>): Promise<ControllerProduct> {
  const res = await fetch(`${API}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Unauthorized');
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Update failed (${res.status})`);
  }
  const data = await res.json() as { product: ControllerProduct };
  return data.product;
}

export async function deleteController(id: string): Promise<void> {
  const res = await fetch(`${API}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Unauthorized');
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Delete failed (${res.status})`);
  }
}

// ── Media ─────────────────────────────────────────────────────────────────────

export async function getControllerUploadUrl(
  productId: string,
  mimeType: string,
): Promise<{ signedUrl: string; path: string; publicUrl: string }> {
  const res = await fetch(`${API}/${productId}/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ mimeType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to get upload URL');
  }
  return res.json();
}

export async function uploadControllerMedia(
  productId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<ControllerMedia> {
  const { signedUrl, path, publicUrl } = await getControllerUploadUrl(productId, file.type);

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    if (onProgress) xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100)); };
    xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });

  const reg = await fetch(`${API}/${productId}/media`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ public_url: publicUrl, storage_path: path, mime_type: file.type }),
  });
  if (!reg.ok) {
    const err = await reg.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to register media');
  }
  const data = await reg.json() as { media: ControllerMedia };
  return data.media;
}

export async function updateControllerMedia(
  productId: string,
  mediaId: string,
  fields: { caption?: string; is_primary?: boolean; sort_order?: number },
): Promise<ControllerMedia> {
  const res = await fetch(`${API}/${productId}/media/${mediaId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Update failed');
  }
  const data = await res.json() as { media: ControllerMedia };
  return data.media;
}

export async function deleteControllerMedia(productId: string, mediaId: string): Promise<void> {
  const res = await fetch(`${API}/${productId}/media/${mediaId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Unauthorized');
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Delete failed');
  }
}

// ── Bulk operations ───────────────────────────────────────────────────────────

export async function bulkUpdatePrices(
  updates: { id: string; price: number; compare_at_price?: number | null }[],
): Promise<void> {
  const res = await fetch(`${API}/bulk-price`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Bulk update failed');
  }
}

export async function reorderControllers(
  orders: { id: string; sort_order: number }[],
): Promise<void> {
  const res = await fetch(`${API}/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ orders }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Reorder failed');
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useControllers(adminMode = false) {
  const [products, setProducts]   = useState<ControllerProduct[]>([]);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchControllers(adminMode);
      setProducts(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [adminMode]);

  useEffect(() => { load(); }, [load]);

  return { products, loading, error, reload: load, setProducts };
}
